import { useFieldArray, type UseFormReturn } from 'react-hook-form'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/common/ui/button'
import { Switch } from '@/common/ui/switch'
import { Edit2, Trash2, Plus } from 'lucide-react'
import BrokeFlexMessageItemEditModal, {
	type NotificationItemFormData,
} from '@/modules/messaging/components/BrokeFlexMessageItemEditModal'
import { CreateBrokeFlexMessageItemDtoType } from '@/modules/messaging/types'
import useMutationUpdateBrokeFlexMessageCampaign from '@/modules/messaging/api/hooks/useMutationUpdateBrokeFlexMessageCampaign'
import type { CampaignFormValues } from '../types'

const ITEM_TYPES = [
	{ value: CreateBrokeFlexMessageItemDtoType.RETURN_LOSS, label: 'คืนยอดเสีย' },
	{ value: CreateBrokeFlexMessageItemDtoType.COMMISSION, label: 'คอมมิชชั่น' },
	{
		value: CreateBrokeFlexMessageItemDtoType.INVITE_FRIEND,
		label: 'ชวนเพื่อน',
	},
	{
		value: CreateBrokeFlexMessageItemDtoType.DAILY_CHECK_IN,
		label: 'เช็คอินรายวัน',
	},
	{
		value: CreateBrokeFlexMessageItemDtoType.LUCKY_WHEEL,
		label: 'วงล้อนำโชค',
	},
	{ value: CreateBrokeFlexMessageItemDtoType.COUPON, label: 'คูปอง' },
	{
		value: CreateBrokeFlexMessageItemDtoType.DEPOSIT_PROMOTION,
		label: 'โปรโมชั่นฝาก',
	},
	{
		value: CreateBrokeFlexMessageItemDtoType.CUSTOM_LINK,
		label: 'ลิงก์กำหนดเอง',
	},
] as const

interface ItemsCompactListProps {
	form: UseFormReturn<CampaignFormValues>
	stageIndex: number
	campaignId?: string
	mode?: 'create' | 'edit'
}

export default function ItemsCompactList({
	form,
	stageIndex,
	campaignId,
	mode,
}: ItemsCompactListProps) {
	const {
		fields: itemFields,
		append: appendItem,
		remove: removeItem,
		update: updateItem,
	} = useFieldArray({
		control: form.control,
		name: `stages.${stageIndex}.items`,
	})

	const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null)
	const [isAddingNew, setIsAddingNew] = useState(false)

	const updateMutation = useMutationUpdateBrokeFlexMessageCampaign({
		onSuccess: () => {
			toast.success('บันทึกรายการสำเร็จ')
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการบันทึกรายการ')
		},
	}) as ReturnType<typeof useMutationUpdateBrokeFlexMessageCampaign>

	console.log('ItemsCompactList render', {
		editingItemIndex,
		isAddingNew,
		itemFieldsLength: itemFields.length,
		shouldShowModal: editingItemIndex !== null,
	})

	const handleAddNew = () => {
		console.log('handleAddNew clicked', { currentLength: itemFields.length })
		setEditingItemIndex(itemFields.length)
		setIsAddingNew(true)
	}

	const handleCloseModal = () => {
		console.log('handleCloseModal called')
		setEditingItemIndex(null)
		setIsAddingNew(false)
	}

	const handleToggleEnabled = (itemIndex: number, currentStatus: boolean) => {
		// Update form state immediately
		form.setValue(
			`stages.${stageIndex}.items.${itemIndex}.enabled`,
			!currentStatus,
		)

		// If in edit mode, save to backend
		if (mode === 'edit' && campaignId) {
			const allStages = form.getValues('stages')

			const stages = allStages.map((stage, idx) => ({
				minutesAfterTrigger: stage.minutesAfterTrigger,
				message: stage.message,
				maxItemsToShow: stage.maxItemsToShow,
				order: idx,
				items: stage.items.map((item, idx) => ({
					type: item.type,
					order: idx,
					eligibilityType: item.eligibilityType,
					minValue: item.minValue,
					displayName: item.displayName,
					icon: item.icon,
					description: item.description,
					message: item.message,
					imageUrl: item.imageUrl,
					promotionId: item.promotionId,
					couponCode: item.couponCode,
					actionUrl: item.actionUrl,
					enabled: item.enabled,
				})),
			}))

			updateMutation.mutate({
				id: campaignId,
				data: { stages },
			} as unknown as Parameters<typeof updateMutation.mutate>[0])
		}
	}

	if (itemFields.length === 0) {
		return (
			<>
				<div className="space-y-3">
					<div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
						ยังไม่มีรายการโบนัส
					</div>
					<Button
						type="button"
						onClick={handleAddNew}
						size="sm"
						variant="outline"
						className="w-full"
					>
						<Plus className="mr-2 h-4 w-4" />
						เพิ่มรายการแรก
					</Button>
				</div>

				{editingItemIndex !== null && (
					<BrokeFlexMessageItemEditModal
						key={`modal-${isAddingNew ? 'new' : editingItemIndex}`}
						open={editingItemIndex !== null}
						onClose={handleCloseModal}
						itemIndex={editingItemIndex}
						isAddingNew={isAddingNew}
						currentItem={
							isAddingNew
								? undefined
								: (itemFields[editingItemIndex] as NotificationItemFormData)
						}
						onSave={(itemData) => {
							console.log('onSave called', { isAddingNew, itemData })
							if (isAddingNew) {
								appendItem({ ...itemData, order: itemFields.length })
							} else {
								updateItem(editingItemIndex, {
									...itemData,
									order: editingItemIndex,
								})
							}
							handleCloseModal()
						}}
					/>
				)}
			</>
		)
	}

	return (
		<>
			<div className="space-y-3">
				<div className="space-y-1 rounded-md border">
					{itemFields.map((item, itemIndex) => {
						const itemType = form.watch(
							`stages.${stageIndex}.items.${itemIndex}.type`,
						) as CreateBrokeFlexMessageItemDtoType
						const displayName = form.watch(
							`stages.${stageIndex}.items.${itemIndex}.displayName`,
						)
						const icon = form.watch(
							`stages.${stageIndex}.items.${itemIndex}.icon`,
						)
						const enabled = form.watch(
							`stages.${stageIndex}.items.${itemIndex}.enabled`,
						)
						const imageUrl = form.watch(
							`stages.${stageIndex}.items.${itemIndex}.imageUrl`,
						)
						const typeName =
							ITEM_TYPES.find((t) => t.value === itemType)?.label || 'รายการ'

						return (
							<div
								key={item.id}
								className="border-b transition-colors last:border-b-0 hover:bg-muted/30"
							>
								<div className="flex items-center gap-2 p-2">
									<span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
										{itemIndex + 1}
									</span>
									{imageUrl ? (
										<img
											src={imageUrl}
											alt={displayName}
											className="h-8 w-14 flex-shrink-0 rounded object-cover"
										/>
									) : (
										<span className="text-sm">{icon}</span>
									)}
									<div className="min-w-0 flex-1">
										<div className="truncate text-sm font-medium">
											{displayName || 'ไม่มีชื่อ'}
										</div>
										<div className="truncate text-xs text-muted-foreground">
											{typeName}
										</div>
									</div>
									<div className="flex items-center gap-1">
										<Switch
											checked={enabled}
											onCheckedChange={() =>
												handleToggleEnabled(itemIndex, enabled)
											}
											className="scale-75"
										/>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											onClick={() => setEditingItemIndex(itemIndex)}
										>
											<Edit2 className="h-3 w-3" />
										</Button>
										{itemFields.length > 1 && (
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="h-7 w-7 text-red-600"
												onClick={() => removeItem(itemIndex)}
											>
												<Trash2 className="h-3 w-3" />
											</Button>
										)}
									</div>
								</div>
							</div>
						)
					})}
				</div>

				<Button
					type="button"
					onClick={handleAddNew}
					size="sm"
					variant="outline"
					className="w-full"
				>
					<Plus className="mr-2 h-4 w-4" />
					เพิ่มรายการ
				</Button>
			</div>

			{editingItemIndex !== null && (
				<BrokeFlexMessageItemEditModal
					open={true}
					onClose={handleCloseModal}
					itemIndex={editingItemIndex}
					isAddingNew={isAddingNew}
					currentItem={
						isAddingNew
							? undefined
							: (itemFields[editingItemIndex] as NotificationItemFormData)
					}
					onSave={async (itemData) => {
						console.log('onSave called', { isAddingNew, itemData })

						// Update form state first
						if (isAddingNew) {
							appendItem({ ...itemData, order: itemFields.length })
						} else {
							updateItem(editingItemIndex, {
								...itemData,
								order: editingItemIndex,
							})
						}

						// If in edit mode, save to backend immediately
						if (mode === 'edit' && campaignId) {
							try {
								const allStages = form.getValues('stages')
								// Update the items in the current stage
								const updatedStages = allStages.map((stage, idx) => {
									if (idx === stageIndex) {
										const updatedItems = [...stage.items]
										if (isAddingNew) {
											updatedItems.push({
												...itemData,
												order: updatedItems.length,
											})
										} else {
											updatedItems[editingItemIndex] = {
												...itemData,
												order: editingItemIndex,
											}
										}
										return { ...stage, items: updatedItems }
									}
									return stage
								})

								const stages = updatedStages.map((stage, idx) => ({
									minutesAfterTrigger: stage.minutesAfterTrigger,
									message: stage.message,
									maxItemsToShow: stage.maxItemsToShow,
									order: idx,
									items: stage.items.map((item, itemIdx) => ({
										type: item.type,
										order: itemIdx,
										eligibilityType: item.eligibilityType,
										minValue: item.minValue,
										displayName: item.displayName,
										icon: item.icon,
										description: item.description,
										message: item.message,
										imageUrl: item.imageUrl,
										promotionId: item.promotionId,
										couponCode: item.couponCode,
										actionUrl: item.actionUrl,
										enabled: item.enabled,
									})),
								}))

								await updateMutation.mutateAsync({
									id: campaignId,
									data: { stages },
								} as unknown as Parameters<
									typeof updateMutation.mutateAsync
								>[0])
							} catch (error) {
								console.error('Error saving to backend:', error)
								// Error toast already shown by mutation
							}
						}

						handleCloseModal()
					}}
				/>
			)}
		</>
	)
}
